import React, { useState } from "react";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Label, StatCard } from "./common";
import { fmtDate } from "../lib/time";

const STAR_COLORS = { 1: "#EF4444", 2: "#F97316", 3: "#F59E0B", 4: "#84CC16", 5: "#10B981" };
export const DIM_LABELS = { friendliness: "Friendliness", speed: "Speed", accuracy: "Accuracy", knowledge: "Knowledge", cleanliness: "Cleanliness" };
const v = (x, suffix = "") => (x === null || x === undefined ? "—" : `${x}${suffix}`);

export function Panel({ title, children, testId, className = "", right }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-5 ${className}`} data-testid={testId}>
      <div className="mb-3 flex items-center justify-between gap-2"><Label>{title}</Label>{right}</div>
      {children}
    </div>
  );
}

export function Distribution({ dist, testId, onPick }) {
  return (
    <div data-testid={testId}>
      <div className="flex h-3 overflow-hidden rounded-full bg-slate-100">
        {[1, 2, 3, 4, 5].map((s) => <div key={s} onClick={onPick ? () => onPick(s) : undefined} className={onPick ? "cursor-pointer hover:opacity-80" : ""} style={{ width: `${dist?.[s] || 0}%`, background: STAR_COLORS[s] }} />)}
      </div>
      <div className="mt-2 grid grid-cols-5 text-center text-xs text-slate-500">
        {[1, 2, 3, 4, 5].map((s) => onPick
          ? <button key={s} onClick={() => onPick(s)} data-testid={`${testId}-star-${s}`} className="rounded py-0.5 hover:bg-slate-100"><b style={{ color: STAR_COLORS[s] }}>{s}★</b> {v(dist?.[s], "%")}</button>
          : <span key={s}><b style={{ color: STAR_COLORS[s] }}>{s}★</b> {v(dist?.[s], "%")}</span>)}
      </div>
    </div>
  );
}

export function SsiTile({ d, testId, label = "Service Index", sub }) {
  return (
    <StatCard label={label} value={v(d?.ssi)} testId={testId}
      sub={d?.band ? `95% range ${d.band.low}–${d.band.high}${sub ? ` · ${sub}` : ""}` : sub || "Not enough ratings for a range"} />
  );
}

export function Touchpoints({ tp, testId, onPick }) {
  const keys = Object.keys(tp || {});
  if (!keys.length) return <p className="text-sm text-slate-400" data-testid={`${testId}-empty`}>No touchpoint scores yet. Choose which ones customers see in Settings.</p>;
  return (
    <div className="space-y-2" data-testid={testId}>
      {keys.map((k) => (
        <div key={k} onClick={onPick ? () => onPick(k) : undefined} data-testid={`${testId}-${k}`}
          className={`flex items-center gap-3 rounded-md text-sm ${onPick ? "cursor-pointer hover:bg-slate-50" : ""}`}>
          <span className="w-28 font-semibold text-slate-700">{DIM_LABELS[k] || k}</span>
          <div className="h-2 flex-1 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-indigo-600" style={{ width: `${(tp[k].avg / 5) * 100}%` }} /></div>
          <span className="w-20 text-right font-semibold text-slate-800">{tp[k].avg} <span className="text-xs font-normal text-slate-400">({tp[k].count})</span></span>
        </div>
      ))}
    </div>
  );
}

export function Themes({ items, testId, empty = "No comment themes yet.", onPick }) {
  if (!items?.length) return <p className="text-sm text-slate-400">{empty}</p>;
  return (
    <ul className="space-y-1.5" data-testid={testId}>
      {items.map((t) => (
        <li key={t.theme} onClick={onPick ? () => onPick(t.theme) : undefined} data-testid={`${testId}-${t.theme}`}
          className={`flex items-center justify-between rounded-md px-1 text-sm ${onPick ? "cursor-pointer hover:bg-slate-50" : ""}`}>
          <span className="font-semibold text-slate-700">{t.label}</span>
          <span className="text-slate-500">{t.mentions} mention{t.mentions === 1 ? "" : "s"}{t.negative ? <span className="ml-2 text-red-500">{t.negative} low</span> : null}</span>
        </li>
      ))}
    </ul>
  );
}

export function Recovery({ r, testId }) {
  return (
    <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4" data-testid={testId}>
      <Mini label="Cases" value={r?.cases} />
      <Mini label={`Acknowledged ≤ ${r?.sla_hours}h`} value={v(r?.within_sla_rate, "%")} />
      <Mini label="Median response" value={r?.median_response_hours == null ? "—" : `${r.median_response_hours} h`} />
      <Mini label="Closure rate" value={v(r?.closure_rate, "%")} />
      {r?.ageing && (
        <div className="col-span-2 sm:col-span-4">
          <p className="text-xs text-slate-400">Open cases by age</p>
          <div className="mt-1 flex flex-wrap gap-2">
            {Object.entries(r.ageing).map(([k, n]) => <span key={k} className={`rounded-full px-2 py-0.5 text-xs font-semibold ${n && (k === ">7d" || k === "3-7d") ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-600"}`}>{k}: {n}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

export function Mini({ label, value, sub }) {
  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <p className="text-xs text-slate-400">{label}</p>
      <p className="sv-metric text-lg font-bold text-slate-900">{value ?? "—"}</p>
      {sub && <p className="text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

export const weekLabel = (w) => `w/c ${fmtDate(w)}`;
export const fmtV = v;

export function PdfButton({ path, filename, testId }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not create the PDF"); }
    finally { setBusy(false); }
  };
  return (
    <Button variant="outline" onClick={go} disabled={busy} data-testid={testId}>
      <FileDown className="mr-1 h-4 w-4" /> {busy ? "Preparing…" : "Download PDF"}
    </Button>
  );
}
